/**
 * Compact Training Distribution card for the Athlete Detail page.
 *
 * Same data source as /staff/reports/training-distribution but reduced to a
 * single donut + KPI strip with a last-30/90-days and season toggle.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { GymBreakdown } from '../gym/GymBreakdown';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
} from 'recharts';
import { BarChart3, Loader2, ExternalLink, Shield } from 'lucide-react';

const CATEGORY_COLORS = {
  Match: '#ef4444', 'Post-Match': '#f472b6', Training: '#10b981', Gym: '#f59e0b',
  Recovery: '#06b6d4', Prevention: '#6366f1', Tactical: '#3b82f6',
  Technical: '#a855f7', Situational: '#f97316', Aerobic: '#84cc16',
  RTP: '#fb923c', VBT: '#c084fc', Other: '#71717a',
};
const fallback = (c) => CATEGORY_COLORS[c] || '#71717a';

const RANGES = [
  { key: '30',  label: 'Last 30d',  params: { last_days: 30 } },
  { key: '90',  label: 'Last 90d',  params: { last_days: 90 } },
  { key: '365', label: 'Last 365d', params: { last_days: 365 } },
  // Current football season (Aug → Jul); resolved at click time.
  { key: 'season', label: 'Season', params: {} },
];

const currentSeasonYear = () => {
  const d = new Date();
  return d.getMonth() >= 7 ? d.getFullYear() : d.getFullYear() - 1;
};

export default function TrainingDistributionCard({ athleteId }) {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [range, setRange] = useState('90');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!athleteId) return;
    setLoading(true);
    const sel = RANGES.find(r => r.key === range);
    const params = { athlete_id: athleteId, ...(sel?.params || {}) };
    if (range === 'season') params.season = currentSeasonYear();
    try {
      const r = await api.get('/training-distribution/athlete', { params });
      setData(r.data);
    } catch {
      setData(null);
    }
    setLoading(false);
  }, [api, athleteId, range]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const donutData = useMemo(() => {
    if (!data) return [];
    return data.breakdown.filter(b => b.minutes > 0).map(b => ({
      name: b.category, value: b.minutes, percentage: b.percentage,
    }));
  }, [data]);

  return (
    <Card className="bg-zinc-900/50 border-zinc-800" data-testid="athlete-training-distribution">
      <CardHeader className="flex flex-row items-center justify-between pb-3 gap-2">
        <CardTitle className="text-sm text-zinc-400 flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-amber-400" /> Training Distribution
        </CardTitle>
        <div className="flex items-center gap-1">
          {RANGES.map(r => (
            <Button key={r.key} size="sm" variant={range === r.key ? 'default' : 'ghost'}
              onClick={() => setRange(r.key)}
              className={`h-7 px-2 text-xs ${range === r.key ? 'bg-amber-500 text-zinc-950 hover:bg-amber-400' : 'text-zinc-400'}`}
              data-testid={`td-range-${r.key}`}>
              {r.label}
            </Button>
          ))}
          <Button size="sm" variant="ghost" onClick={() => navigate('/staff/reports/training-distribution')}
            className="h-7 px-2 text-xs text-zinc-400 hover:text-zinc-100" title="Open full report"
            data-testid="td-open-full">
            <ExternalLink className="w-3.5 h-3.5" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center h-48 text-zinc-500">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
          </div>
        ) : !data || data.total_sessions === 0 ? (
          <div className="text-center text-zinc-500 text-sm py-10" data-testid="td-card-empty">
            No sessions in this range.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Donut */}
            <div className="h-56" data-testid="td-card-donut">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={donutData} dataKey="value" nameKey="name"
                    cx="50%" cy="50%" innerRadius={45} outerRadius={75}
                    labelLine={false}>
                    {donutData.map(e => <Cell key={e.name} fill={fallback(e.name)} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', color: '#fff' }}
                    formatter={(v, n) => [`${v.toLocaleString()} min`, n]} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-col justify-center gap-2 text-xs">
              <KPI label="Total Sessions" value={data.total_sessions} />
              <KPI label="Total Minutes" value={data.total_minutes.toLocaleString()} />
              <KPI label="RTP Sessions" value={data.rtp_sessions} icon={Shield} accent />
              <KPI label="RTP Minutes" value={data.rtp_minutes.toLocaleString()} icon={Shield} accent />
              <KPI label="RTP % of Load" value={`${data.rtp_percentage}%`} icon={Shield} accent />
            </div>
            <div className="sm:col-span-2 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1.5 mt-1">
              {data.breakdown.filter(b => b.minutes > 0).map(b => (
                <div key={b.category} className="flex items-center gap-2 text-[11px] text-zinc-300" data-testid={`td-card-row-${b.category}`}>
                  <span className="w-2 h-2 rounded-full" style={{ background: fallback(b.category) }} />
                  <span className={b.category === 'RTP' ? 'text-amber-300 font-medium' : ''}>{b.category}</span>
                  <span className="text-zinc-500">{b.minutes}m ({b.percentage}%)</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <GymBreakdown data={data?.gym_breakdown} prefix="profile-gym-breakdown" />
      </CardContent>
    </Card>
  );
}

function KPI({ label, value, icon: Icon, accent }) {
  return (
    <div className={`flex items-center justify-between px-3 py-1.5 rounded ${accent ? 'bg-amber-500/10 text-amber-300' : 'bg-zinc-800/40 text-zinc-200'}`}>
      <span className="flex items-center gap-1.5">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </span>
      <span className="font-semibold">{value}</span>
    </div>
  );
}
